import { useState, useEffect } from 'react';

interface MediaMessageProps {
  attachedMedia: Array<{ getContentTemporaryUrl: () => Promise<string | null> }>;
}

interface ImageState {
  url: string | null;
  loading: boolean;
  error: boolean;
}

export function MediaMessage({ attachedMedia }: MediaMessageProps) {
  const [imageStates, setImageStates] = useState<ImageState[]>(
    attachedMedia.map(() => ({ url: null, loading: true, error: false }))
  );

  useEffect(() => {
    let cancelled = false;

    const resolveUrls = async () => {
      const results = await Promise.allSettled(
        attachedMedia.map(media => media.getContentTemporaryUrl())
      );

      if (cancelled) return;

      setImageStates(
        results.map(result =>
          result.status === 'fulfilled'
            ? { url: result.value, loading: false, error: false }
            : { url: null, loading: false, error: true }
        )
      );
    };

    resolveUrls();

    return () => {
      cancelled = true;
    };
  }, [attachedMedia]);

  if (imageStates.length === 0) return null;

  const isSingle = imageStates.length === 1;

  return (
    <div className={isSingle ? 'w-full' : 'grid grid-cols-2 gap-1'}>
      {imageStates.map((state, index) => (
        <div
          key={index}
          className={`relative overflow-hidden rounded-xl ${isSingle ? 'w-full' : 'aspect-square'}`}
        >
          {state.loading && (
            <div className="w-full h-32 bg-white/10 animate-pulse rounded-xl" />
          )}
          {state.error && (
            <div className="w-full h-32 bg-white/5 flex items-center justify-center rounded-xl border border-white/10">
              <span className="text-white/40 text-xs">Image unavailable</span>
            </div>
          )}
          {state.url && !state.loading && !state.error && (
            <a
              href={state.url}
              target="_blank"
              rel="noopener noreferrer"
              className="block"
            >
              <img
                src={state.url}
                alt={`Attachment ${index + 1}`}
                className="w-full object-cover rounded-xl hover:opacity-90 transition-opacity"
                onError={() => {
                  setImageStates(prev => {
                    const next = [...prev];
                    next[index] = { ...next[index], error: true };
                    return next;
                  });
                }}
              />
            </a>
          )}
        </div>
      ))}
    </div>
  );
}
